import { sanitizeAnalyticsUrl } from '@/lib/analyticsUrl';

const STORAGE_KEY = 'figtures_lead_attribution';
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

export type ReferrerClass =
  | 'chatgpt'
  | 'perplexity'
  | 'bing_copilot'
  | 'claude'
  | 'gemini'
  | 'google'
  | 'bing'
  | 'search'
  | 'linkedin'
  | 'facebook'
  | 'instagram'
  | 'social'
  | 'internal'
  | 'direct'
  | 'referral'
  | 'unknown';

/** Marketing channel. `paid` requires a click id or a paid utm_medium — utm_source alone never counts. */
export type LeadChannel = 'paid' | 'ai_assistant' | 'organic_search' | 'social' | 'referral' | 'direct';

export type LeadAttribution = {
  source: string;
  channel?: LeadChannel;
  toolId?: string;
  /** Raw `project_type` query from tool CTA (before contact select mapping). */
  projectTypeRaw?: string;
  blogSlug?: string;
  landingPath?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  documentReferrer?: string;
  referrerHost?: string;
  referrerClass?: ReferrerClass;
  /** Cost tool band lines for contact prefill (F19). sessionStorage only — never put in a URL. */
  toolEstimateSummary?: string;
  capturedAt: number;
};

export const CONTACT_EMAILJS_FIELD_KEYS = [
  'title',
  'name',
  'email',
  'from_name',
  'from_email',
  'message',
  'to_email',
  'reply_to',
  'lead_id',
  'lead_source',
  'lead_channel',
  'tool_id',
  'project_type',
  'blog_slug',
  'landing_path',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'http_referrer',
  'referrer_host',
  'referrer_class',
  'hear_about',
] as const;

const MAX_TOOL_ESTIMATE_SUMMARY_CHARS = 2000;

export const HEAR_ABOUT_OPTION_IDS = [
  'search_google',
  'ai_assistant',
  'linkedin',
  'instagram',
  'referral',
  'blog',
  'event',
  'other',
] as const;

export type HearAboutOptionId = (typeof HEAR_ABOUT_OPTION_IDS)[number];

function parseStored(raw: string | null): LeadAttribution | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as LeadAttribution;
    if (!parsed?.source || typeof parsed.capturedAt !== 'number') return null;
    if (Date.now() - parsed.capturedAt > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function stripWww(host: string) {
  return host.replace(/^www\./i, '').toLowerCase();
}

/** Classify external referrer host for CRM + GA4 (first-party; works before cookie consent). */
export function classifyReferrerHost(referrerUrl: string): {
  host: string;
  class: ReferrerClass;
} {
  try {
    const url = new URL(referrerUrl);
    const host = stripWww(url.hostname);
    return { host, class: classifyHostToken(host) ?? 'referral' };
  } catch {
    return { host: '', class: 'unknown' };
  }
}

function hostIs(host: string, domain: string) {
  return host === domain || host.endsWith(`.${domain}`);
}

/** Works for referrer hostnames and utm_source values (`chatgpt.com`, `chatgpt`, `google`). */
function classifyHostToken(raw: string): ReferrerClass | null {
  const host = stripWww(raw.trim().replace(/^https?:\/\//i, '').split(/[/?#]/)[0] ?? '');
  if (!host) return null;
  if (hostIs(host, 'figtures.com') || host === 'localhost') return 'internal';
  if (host === 'chatgpt' || hostIs(host, 'chatgpt.com') || hostIs(host, 'openai.com')) return 'chatgpt';
  if (host.includes('perplexity')) return 'perplexity';
  if (host === 'copilot' || hostIs(host, 'copilot.microsoft.com') || hostIs(host, 'copilot.cloud.microsoft')) {
    return 'bing_copilot';
  }
  if (host === 'claude' || hostIs(host, 'claude.ai')) return 'claude';
  if (host === 'gemini' || hostIs(host, 'gemini.google.com') || hostIs(host, 'bard.google.com')) {
    return 'gemini';
  }
  if (host === 'google' || /(^|\.)google\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host)) return 'google';
  if (host === 'bing' || hostIs(host, 'bing.com')) return 'bing';
  if (
    ['yandex', 'duckduckgo', 'yahoo', 'ecosia', 'baidu', 'brave'].includes(host) ||
    /(^|\.)(yandex\.[a-z.]+|duckduckgo\.com|yahoo\.com|ecosia\.org|baidu\.com|search\.brave\.com)$/.test(host)
  ) {
    return 'search';
  }
  if (host === 'linkedin' || hostIs(host, 'linkedin.com') || host === 'lnkd.in') return 'linkedin';
  if (host === 'facebook' || host === 'fb' || hostIs(host, 'facebook.com') || hostIs(host, 'fb.com')) {
    return 'facebook';
  }
  if (host === 'instagram' || hostIs(host, 'instagram.com')) return 'instagram';
  if (
    ['twitter', 'x', 'youtube', 'tiktok', 'reddit', 'pinterest', 'threads'].includes(host) ||
    ['t.co', 'x.com', 'twitter.com', 'youtube.com', 'youtu.be', 'tiktok.com', 'reddit.com', 'pinterest.com', 'threads.net']
      .some((domain) => hostIs(host, domain))
  ) {
    return 'social';
  }
  return null;
}

const AI_CLASSES: ReadonlySet<ReferrerClass> = new Set(['chatgpt', 'perplexity', 'bing_copilot', 'claude', 'gemini']);
const SEARCH_CLASSES: ReadonlySet<ReferrerClass> = new Set(['google', 'bing', 'search']);
const SOCIAL_CLASSES: ReadonlySet<ReferrerClass> = new Set(['linkedin', 'facebook', 'instagram', 'social']);
const PAID_MEDIUMS = new Set(['cpc', 'ppc', 'cpm', 'paid', 'paidsocial', 'paidsearch', 'display']);
const SOCIAL_MEDIUMS = new Set(['social', 'socialmedia', 'sm', 'organicsocial']);

export type LeadChannelInput = {
  utmSource?: string;
  utmMedium?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  referrerHost?: string;
};

export function classifyLeadChannel(input: LeadChannelInput): LeadChannel {
  if (input.gclid || input.gbraid || input.wbraid || input.fbclid || input.msclkid) return 'paid';
  const medium = input.utmMedium?.trim().toLowerCase().replace(/[\s_-]/g, '') ?? '';
  if (PAID_MEDIUMS.has(medium)) return 'paid';

  const classes = [input.utmSource, input.referrerHost]
    .map((value) => (value ? classifyHostToken(value) : null))
    .filter((value): value is ReferrerClass => Boolean(value) && value !== 'internal');

  if (classes.some((value) => AI_CLASSES.has(value))) return 'ai_assistant';
  if (SOCIAL_MEDIUMS.has(medium)) return 'social';
  if (medium === 'organic') return 'organic_search';
  if (classes.some((value) => SEARCH_CLASSES.has(value))) return 'organic_search';
  if (classes.some((value) => SOCIAL_CLASSES.has(value))) return 'social';

  const externalReferrer = input.referrerHost && classifyHostToken(input.referrerHost) !== 'internal';
  if (input.utmSource?.trim() || medium || externalReferrer) return 'referral';
  return 'direct';
}

/** `lead_source` value for a channel; `paid_ads` keeps the historical GA4/email value. */
export function sourceFromChannel(channel: LeadChannel): string {
  return channel === 'paid' ? 'paid_ads' : channel;
}

/** Explicit on-site entry points that keep their source label; the channel is tracked separately. */
const ON_SITE_SOURCES = new Set(['tool', 'blog', 'blog_lead_magnet']);

type CampaignFields = Pick<
  LeadAttribution,
  'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent' | 'gclid' | 'gbraid' | 'wbraid' | 'fbclid' | 'msclkid'
>;

function readCampaignParams(params: URLSearchParams): CampaignFields {
  const get = (key: string) => params.get(key)?.trim().slice(0, 100) || undefined;
  return {
    utmSource: get('utm_source'),
    utmMedium: get('utm_medium'),
    utmCampaign: get('utm_campaign'),
    utmTerm: get('utm_term'),
    utmContent: get('utm_content'),
    gclid: get('gclid'),
    gbraid: get('gbraid'),
    wbraid: get('wbraid'),
    fbclid: get('fbclid'),
    msclkid: get('msclkid'),
  };
}

function pickCampaign(attribution: LeadAttribution | null | undefined): CampaignFields {
  return {
    utmSource: attribution?.utmSource,
    utmMedium: attribution?.utmMedium,
    utmCampaign: attribution?.utmCampaign,
    utmTerm: attribution?.utmTerm,
    utmContent: attribution?.utmContent,
    gclid: attribution?.gclid,
    gbraid: attribution?.gbraid,
    wbraid: attribution?.wbraid,
    fbclid: attribution?.fbclid,
    msclkid: attribution?.msclkid,
  };
}

function hasCampaign(fields: CampaignFields | LeadAttribution | null | undefined) {
  return Boolean(fields && Object.values(pickCampaign(fields as LeadAttribution)).some(Boolean));
}

function generateLeadIdSuffix() {
  const bytes = new Uint8Array(4);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** Per-submission id shown in the email subject/body and sent with generate_lead (no PII). */
export function generateLeadId(now: Date = new Date()) {
  const day = now.toISOString().slice(0, 10).replace(/-/g, '');
  return `FT-${day}-${generateLeadIdSuffix()}`;
}

function currentLandingPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return sanitizeAnalyticsUrl(window.location.pathname + window.location.search) || undefined;
}

export function persistLeadAttribution(attribution: Omit<LeadAttribution, 'capturedAt'>) {
  if (typeof window === 'undefined') return;
  const payload: LeadAttribution = {
    ...attribution,
    landingPath: attribution.landingPath ? sanitizeAnalyticsUrl(attribution.landingPath) || undefined : undefined,
    capturedAt: Date.now(),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function persistBlogLeadSlug(blogSlug: string) {
  const prior = readLeadAttribution();
  persistLeadAttribution({
    ...pickCampaign(prior),
    channel: prior?.channel,
    documentReferrer: prior?.documentReferrer,
    referrerHost: prior?.referrerHost,
    referrerClass: prior?.referrerClass,
    source: 'blog',
    blogSlug,
    landingPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

export function readLeadAttribution(): LeadAttribution | null {
  if (typeof window === 'undefined') return null;
  const stored = parseStored(sessionStorage.getItem(STORAGE_KEY));
  if (stored?.landingPath) {
    stored.landingPath = sanitizeAnalyticsUrl(stored.landingPath) || undefined;
  }
  return stored;
}

/**
 * Capture landing-page UTM / click ids and document.referrer on every full page load (before GTM may
 * load). A new external touch (campaign params or external referrer) re-classifies the channel; an
 * internal or empty referrer keeps what is already stored.
 */
export function captureInitialVisitAttribution(): LeadAttribution | null {
  if (typeof window === 'undefined') return null;

  const existing = readLeadAttribution();
  const landingPath = currentLandingPath();
  const campaign = readCampaignParams(new URLSearchParams(window.location.search));
  const newCampaign = hasCampaign(campaign);
  const documentReferrer = document.referrer?.trim() || '';
  const referrer = documentReferrer ? classifyReferrerHost(documentReferrer) : null;
  const externalReferrer = referrer && referrer.class !== 'internal' ? referrer : null;

  if (!newCampaign && !externalReferrer) {
    if (existing) return existing;
    persistLeadAttribution({ source: 'direct', channel: 'direct', landingPath });
    return readLeadAttribution();
  }

  const referrerFields = externalReferrer
    ? { documentReferrer, referrerHost: externalReferrer.host || undefined, referrerClass: externalReferrer.class }
    : {
        documentReferrer: existing?.documentReferrer,
        referrerHost: existing?.referrerHost,
        referrerClass: existing?.referrerClass,
      };

  if (existing && !newCampaign && hasCampaign(existing)) {
    if (!existing.documentReferrer) persistLeadAttribution({ ...existing, ...referrerFields });
    return readLeadAttribution();
  }

  const campaignFields = newCampaign ? campaign : pickCampaign(existing);
  const channel = classifyLeadChannel({ ...campaignFields, referrerHost: referrerFields.referrerHost });
  persistLeadAttribution({
    ...existing,
    ...campaignFields,
    ...referrerFields,
    channel,
    landingPath: newCampaign ? landingPath : (existing?.landingPath ?? landingPath),
    source: existing?.source && ON_SITE_SOURCES.has(existing.source) ? existing.source : sourceFromChannel(channel),
  });
  return readLeadAttribution();
}

export function mergeLeadAttributionFromQuery(searchParams: URLSearchParams): LeadAttribution | null {
  captureInitialVisitAttribution();

  const source = searchParams.get('source') || searchParams.get('from');
  const toolId = searchParams.get('tool_id')?.trim() || undefined;
  const projectTypeRaw = searchParams.get('project_type')?.trim().slice(0, 100) || undefined;
  const blogSlug = searchParams.get('blog_slug') || searchParams.get('slug');
  const queryCampaign = readCampaignParams(searchParams);
  const queryHasCampaign = hasCampaign(queryCampaign);

  const hasSignal = Boolean(source || toolId || projectTypeRaw || blogSlug || queryHasCampaign);
  if (!hasSignal) return readLeadAttribution();

  const prior = readLeadAttribution();
  const campaignFields = queryHasCampaign ? queryCampaign : pickCampaign(prior);
  const channel = classifyLeadChannel({ ...campaignFields, referrerHost: prior?.referrerHost });

  const resolvedSource = source || (blogSlug ? 'blog' : sourceFromChannel(channel));
  // estimate_summary is intentionally not read from the URL; the tool hands it over via sessionStorage.
  const toolEstimateSummary = prior?.toolEstimateSummary?.slice(0, MAX_TOOL_ESTIMATE_SUMMARY_CHARS);

  const merged: LeadAttribution = {
    source: resolvedSource,
    channel,
    toolId,
    projectTypeRaw,
    toolEstimateSummary,
    blogSlug: blogSlug || undefined,
    landingPath: currentLandingPath(),
    ...campaignFields,
    documentReferrer: prior?.documentReferrer,
    referrerHost: prior?.referrerHost,
    referrerClass: prior?.referrerClass,
    capturedAt: Date.now(),
  };

  persistLeadAttribution(merged);
  return merged;
}

function hasUtmData(attribution: LeadAttribution | null) {
  return hasCampaign(attribution);
}

function shouldShowAttributionBlock(
  attribution: LeadAttribution | null,
  hearAbout?: string,
) {
  if (hearAbout?.trim()) return true;
  if (!attribution) return false;
  if (attribution.blogSlug || hasUtmData(attribution)) return true;
  if (attribution.documentReferrer || attribution.referrerClass) return true;
  if (
    attribution.source &&
    !['contact', 'direct', 'contact_form'].includes(attribution.source)
  ) {
    return true;
  }
  return false;
}

export function formatLeadAttributionForEmail(
  attribution: LeadAttribution | null,
  lang: 'tr' | 'en',
  hearAbout?: string,
  leadId?: string,
) {
  if (!leadId && !shouldShowAttributionBlock(attribution, hearAbout)) return '';

  const isLeadMagnet = attribution?.source === 'blog_lead_magnet';
  const clickIds = [
    attribution?.gbraid ? `gbraid: ${attribution.gbraid}` : null,
    attribution?.wbraid ? `wbraid: ${attribution.wbraid}` : null,
    attribution?.msclkid ? `Microsoft Click ID: ${attribution.msclkid}` : null,
  ];

  if (lang === 'tr') {
    return `\n\n${[
      '---',
      leadId ? `Lead ID: ${leadId}` : null,
      `Lead kaynağı: ${attribution?.source ?? 'direct'}`,
      attribution?.channel ? `Kanal: ${attribution.channel}` : null,
      hearAbout?.trim() ? `Bizi nereden duydunuz: ${hearAbout.trim()}` : null,
      attribution?.referrerClass ? `Referrer sınıfı: ${attribution.referrerClass}` : null,
      attribution?.referrerHost ? `Referrer host: ${attribution.referrerHost}` : null,
      attribution?.documentReferrer ? `HTTP referrer: ${attribution.documentReferrer}` : null,
      attribution?.blogSlug ? `Blog yazısı: ${attribution.blogSlug}` : null,
      attribution?.toolId ? `Araç: ${attribution.toolId}` : null,
      attribution?.projectTypeRaw ? `Proje türü (araç): ${attribution.projectTypeRaw}` : null,
      attribution?.toolEstimateSummary
        ? `Maliyet aracı bant özeti:\n${attribution.toolEstimateSummary}`
        : null,
      isLeadMagnet ? 'Tür: Ücretsiz kaynak indirme' : null,
      attribution?.utmSource ? `UTM source: ${attribution.utmSource}` : null,
      attribution?.utmMedium ? `UTM medium: ${attribution.utmMedium}` : null,
      attribution?.utmCampaign ? `UTM campaign: ${attribution.utmCampaign}` : null,
      attribution?.utmTerm ? `UTM term: ${attribution.utmTerm}` : null,
      attribution?.utmContent ? `UTM content: ${attribution.utmContent}` : null,
      attribution?.gclid ? `Google Click ID: ${attribution.gclid}` : null,
      attribution?.fbclid ? `Meta Click ID: ${attribution.fbclid}` : null,
      ...clickIds,
      attribution?.landingPath ? `Giriş URL: ${attribution.landingPath}` : null,
    ]
      .filter(Boolean)
      .join('\n')}`;
  }

  return `\n\n${[
    '---',
    leadId ? `Lead ID: ${leadId}` : null,
    `Lead source: ${attribution?.source ?? 'direct'}`,
    attribution?.channel ? `Channel: ${attribution.channel}` : null,
    hearAbout?.trim() ? `How did you hear about us: ${hearAbout.trim()}` : null,
    attribution?.referrerClass ? `Referrer class: ${attribution.referrerClass}` : null,
    attribution?.referrerHost ? `Referrer host: ${attribution.referrerHost}` : null,
    attribution?.documentReferrer ? `HTTP referrer: ${attribution.documentReferrer}` : null,
    attribution?.blogSlug ? `Blog post: ${attribution.blogSlug}` : null,
    attribution?.toolId ? `Tool: ${attribution.toolId}` : null,
    attribution?.projectTypeRaw ? `Project type (tool): ${attribution.projectTypeRaw}` : null,
    attribution?.toolEstimateSummary
      ? `Cost tool band summary:\n${attribution.toolEstimateSummary}`
      : null,
    isLeadMagnet ? 'Type: Free resource download' : null,
    attribution?.utmSource ? `UTM source: ${attribution.utmSource}` : null,
    attribution?.utmMedium ? `UTM medium: ${attribution.utmMedium}` : null,
    attribution?.utmCampaign ? `UTM campaign: ${attribution.utmCampaign}` : null,
    attribution?.utmTerm ? `UTM term: ${attribution.utmTerm}` : null,
    attribution?.utmContent ? `UTM content: ${attribution.utmContent}` : null,
    attribution?.gclid ? `Google Click ID: ${attribution.gclid}` : null,
    attribution?.fbclid ? `Meta Click ID: ${attribution.fbclid}` : null,
    ...clickIds,
    attribution?.landingPath ? `Landing URL: ${attribution.landingPath}` : null,
  ]
    .filter(Boolean)
    .join('\n')}`;
}

export function buildContactUrlFromBlog(blogSlug: string) {
  return `/contact?source=blog&blog_slug=${encodeURIComponent(blogSlug)}`;
}

export type ContactProjectTypeLabels = {
  ai: string;
  saas: string;
  mobile: string;
  custom: string;
  other: string;
};

/** Map free-text tool `project_type` query to contact form select labels (no env). */
/** Visible summary when user lands from cost tool CTA (`source=tool`). */
export function formatToolLeadSummary(
  attribution: LeadAttribution | null | undefined,
  projectTypeLabel: string,
  lang: 'tr' | 'en',
): string | null {
  if (attribution?.source !== 'tool') return null;
  const projectLabel = projectTypeLabel.trim() || attribution.projectTypeRaw?.trim() || '';
  if (!projectLabel && !attribution.toolId) return null;
  const toolSuffix = attribution.toolId ? ` (${attribution.toolId})` : '';
  if (lang === 'tr') {
    return projectLabel
      ? `Maliyet aracından talep${toolSuffix}: ${projectLabel}`
      : `Maliyet aracından talep${toolSuffix}`;
  }
  return projectLabel
    ? `Request from cost tool${toolSuffix}: ${projectLabel}`
    : `Request from cost tool${toolSuffix}`;
}

export type ContactProjectTypeId = keyof ContactProjectTypeLabels;

/** Map free text (e.g. cost tool input) to a contact project type id — safe to put in a URL. */
export function classifyContactProjectType(raw: string | null | undefined): ContactProjectTypeId | '' {
  const norm = raw?.trim().toLowerCase();
  if (!norm) return '';
  if (/(saas|mvp)/i.test(norm)) return 'saas';
  if (/(mobil|mobile|\bapp\b|uygulama)/i.test(norm)) return 'mobile';
  if (/(^ai\b|automation|otomasyon|yapay)/i.test(norm)) return 'ai';
  if (/(custom|özel|ozel|yazılım|yazilim)/i.test(norm)) return 'custom';
  return 'other';
}

export function resolveContactProjectTypePrefill(
  rawQuery: string | null | undefined,
  labels: ContactProjectTypeLabels,
): string {
  const raw = rawQuery?.trim();
  if (!raw) return '';

  const known = Object.values(labels);
  if (known.includes(raw)) return raw;

  const id = classifyContactProjectType(raw);
  return id ? labels[id] : labels.other;
}

/** Prefill contact message when user arrives from cost tool with band lines (F19). */
export function buildToolEstimateMessagePrefill(
  estimateSummary: string | null | undefined,
  lang: 'tr' | 'en',
): string {
  const body = estimateSummary?.trim();
  if (!body) return '';
  const header =
    lang === 'tr'
      ? 'Maliyet aracından paylaştığım özet bantlar:'
      : 'Budget bands from the cost estimator:';
  return `${header}\n${body}`;
}

export type ContactEmailJsPayloadInput = {
  lang: 'tr' | 'en';
  trimmed: { name: string; email: string; message: string; projectType: string };
  leadAttribution: LeadAttribution | null;
  hearAboutLabel: string;
  emailTitle: string;
  targetEmail: string;
  leadId: string;
};

/** EmailJS template params — keep in sync with docs/contact-emailjs.md (F45). */
export function buildContactEmailJsParams(input: ContactEmailJsPayloadInput) {
  const { lang, trimmed, leadAttribution, hearAboutLabel, emailTitle, targetEmail, leadId } = input;
  const attributionFooter = formatLeadAttributionForEmail(
    leadAttribution,
    lang,
    hearAboutLabel,
    leadId,
  );

  const messageBody = [
    trimmed.message,
    trimmed.projectType
      ? lang === 'tr'
        ? `\n\nProje türü: ${trimmed.projectType}`
        : `\n\nProject type: ${trimmed.projectType}`
      : '',
    attributionFooter,
  ].join('');

  return {
    title: `${emailTitle} [${leadId}]`,
    name: trimmed.name,
    email: trimmed.email,
    from_name: trimmed.name,
    from_email: trimmed.email,
    message: messageBody,
    to_email: targetEmail,
    reply_to: trimmed.email,
    lead_id: leadId,
    lead_source: leadAttribution?.source ?? 'contact_form',
    lead_channel: leadAttribution?.channel ?? '',
    tool_id: leadAttribution?.toolId ?? '',
    project_type: trimmed.projectType || leadAttribution?.projectTypeRaw || '',
    blog_slug: leadAttribution?.blogSlug ?? '',
    landing_path: leadAttribution?.landingPath ?? '',
    utm_source: leadAttribution?.utmSource ?? '',
    utm_medium: leadAttribution?.utmMedium ?? '',
    utm_campaign: leadAttribution?.utmCampaign ?? '',
    utm_term: leadAttribution?.utmTerm ?? '',
    utm_content: leadAttribution?.utmContent ?? '',
    gclid: leadAttribution?.gclid ?? '',
    fbclid: leadAttribution?.fbclid ?? '',
    http_referrer: leadAttribution?.documentReferrer ?? '',
    referrer_host: leadAttribution?.referrerHost ?? '',
    referrer_class: leadAttribution?.referrerClass ?? '',
    hear_about: hearAboutLabel,
  };
}

export const LEAD_MAGNET_EMAILJS_FIELD_KEYS = [
  ...CONTACT_EMAILJS_FIELD_KEYS,
  'lead_magnet_id',
] as const;

export type LeadMagnetEmailJsPayloadInput = {
  lang: 'tr' | 'en';
  email: string;
  blogSlug: string;
  magnetId: string;
  resourceTitle: string;
  downloadUrl: string;
  leadAttribution: LeadAttribution | null;
  targetEmail: string;
  leadId: string;
};

/** Blog lead magnet EmailJS params — same template and field set as the contact form (+ lead_magnet_id). */
export function buildLeadMagnetEmailJsParams(input: LeadMagnetEmailJsPayloadInput) {
  const { lang, email, blogSlug, magnetId, resourceTitle, downloadUrl, leadAttribution, targetEmail, leadId } =
    input;
  const attribution: LeadAttribution = {
    ...(leadAttribution ?? { capturedAt: Date.now() }),
    source: 'blog_lead_magnet',
    blogSlug,
    toolId: undefined,
    projectTypeRaw: undefined,
    toolEstimateSummary: undefined,
  };
  const displayName = `Blog lead magnet (${email})`;
  const message = [
    lang === 'tr' ? 'Blog kaynak indirme talebi' : 'Blog resource download request',
    `${lang === 'tr' ? 'Kaynak' : 'Resource'}: ${resourceTitle} (${magnetId})`,
    `Blog: ${blogSlug}`,
    `${lang === 'tr' ? 'Dil' : 'Language'}: ${lang}`,
    `${lang === 'tr' ? 'İndirme' : 'Download'}: ${downloadUrl}`,
    formatLeadAttributionForEmail(attribution, lang, undefined, leadId),
  ].join('\n');

  return {
    title: `Lead Magnet: ${resourceTitle} [${leadId}]`,
    name: displayName,
    email,
    from_name: displayName,
    from_email: email,
    message,
    to_email: targetEmail,
    reply_to: email,
    lead_id: leadId,
    lead_source: 'blog_lead_magnet',
    lead_channel: attribution.channel ?? '',
    tool_id: '',
    project_type: '',
    blog_slug: blogSlug,
    landing_path: attribution.landingPath ? sanitizeAnalyticsUrl(attribution.landingPath) : '',
    utm_source: attribution.utmSource ?? '',
    utm_medium: attribution.utmMedium ?? '',
    utm_campaign: attribution.utmCampaign ?? '',
    utm_term: attribution.utmTerm ?? '',
    utm_content: attribution.utmContent ?? '',
    gclid: attribution.gclid ?? '',
    fbclid: attribution.fbclid ?? '',
    http_referrer: attribution.documentReferrer ?? '',
    referrer_host: attribution.referrerHost ?? '',
    referrer_class: attribution.referrerClass ?? '',
    hear_about: '',
    lead_magnet_id: magnetId,
  };
}
